import {FILE_OBJECT} from 'common'
import {StorageError} from 'firebase/storage'
import {ERRORS_CODE_MESSAGES_MAP, FirebaseInstances} from './helpers'
import {categoriesApi, storageApi} from 'lib/api'
import {resolveStorageMetadataTarget} from 'lib/media/storageUrl'

function uploadToSignedUrl(
  uploadUrl: string,
  headers: Record<string, string>,
  file: File,
  onProgressUpdate?: (progress: number) => void
) {
  return new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('PUT', uploadUrl)
    Object.entries(headers).forEach(([key, value]) =>
      request.setRequestHeader(key, value)
    )
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgressUpdate?.((event.loaded / event.total) * 100)
      }
    }
    request.onerror = () => reject(new Error('Upload failed'))
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) resolve()
      else reject(new Error('Upload failed'))
    }
    request.send(file)
  })
}

export function createStorageService(fb: FirebaseInstances) {
  const getFileInfoFromStorage = async (
    filePath: string
  ): Promise<FILE_OBJECT | null> => {
    const target = resolveStorageMetadataTarget(filePath)
    if (!target) return null

    try {
      const object = target.productId
        ? await storageApi.getProductObjectInfo(
            target.productId,
            target.objectPath
          )
        : await storageApi.getAdminAssetObjectInfo(target.objectPath)
      const fileSizeInBytes = object.size
      const sizeInMB = fileSizeInBytes / 1024 ** 2

      return {
        type: object.contentType,
        mbSize: sizeInMB,
        name: object.name,
        URL: object.url,
        downloadURL: object.url,
        originalName: object.originalName
      }
    } catch (error) {
      console.error('Error retrieving file info:', error)
      return null
    }
  }

  const uploadFile = async ({
    file,
    onFileUpload,
    fileName,
    fileType,
    isCategory,
    onError,
    onProgressUpdate,
    filePath,
    productId
  }: {
    file: File
    onError?: (error?: StorageError) => void
    onFileUpload: (downloadURL?: string) => void
    onProgressUpdate?: (progress: number) => void
    productId?: string
    fileType?: string
    fileName?: string
    filePath?: string
    isCategory?: boolean
  }) => {
    const nameOfFile = fileName || file.name || ''
    const destinationPath = filePath
      ? filePath
      : fileType
      ? isCategory
        ? `categories/${productId}/image/category-image`
        : `products/${productId}/${fileType}/${nameOfFile}`
      : nameOfFile

    try {
      const intent = await storageApi.createUploadIntent({
        filePath: destinationPath,
        contentType: file.type,
        size: file.size,
        originalName: file.name
      })
      await uploadToSignedUrl(
        intent.uploadUrl,
        intent.headers,
        file,
        onProgressUpdate
      )
      const completed = await storageApi.completeUpload({
        quarantinePath: intent.quarantinePath,
        filePath: intent.filePath
      })
      onProgressUpdate?.(100)
      onFileUpload?.(completed.url)
      return {success: true}
    } catch (error: any) {
      onError?.(error as StorageError)
      const code = error.code
      return {
        error: {
          success: false,
          code,
          message: ERRORS_CODE_MESSAGES_MAP[code]
        }
      }
    }
  }

  const deleteFile = async ({
    fileName,
    fileType,
    isCategory,
    productID,
    filePath
  }: {
    fileName?: string
    productID?: string
    fileType?: string
    isCategory?: boolean
    filePath?: string
  }) => {
    const destinationPath = filePath
      ? filePath
      : productID && fileType
      ? isCategory
        ? `categories/${productID}/image/category-image`
        : `products/${productID}/${fileType}/${fileName}`
      : fileName || ''
    try {
      await storageApi.deleteObject(destinationPath)
      if (isCategory) {
        const placeHolderImage =
          'https://firebasestorage.googleapis.com/v0/b/dadeauctions.appspot.com/o/assets%2FImagePlaceholder.png?alt=media&token=d33e0dbc-6ae2-403b-b8af-d689a6304235'
        await categoriesApi.update(productID || '', {
          imageUrl: placeHolderImage
        })
      }
      return {}
    } catch (error) {
      return {
        error
      }
    }
  }

  return {
    getFileInfoFromStorage,
    uploadFile,
    deleteFile
  }
}
